import type { NavSectionProps } from 'src/components/nav-section';

import { paths } from 'src/routes/paths';

import { CONFIG } from 'src/global-config';

import { Iconify } from 'src/components/iconify';
import { SvgColor } from 'src/components/svg-color';

// ----------------------------------------------------------------------

const icon = (name: string) => (
  <SvgColor src={`${CONFIG.assetsDir}/assets/icons/navbar/${name}.svg`} />
);

const ICONS = {
  file: icon('ic-file'),
  external: icon('ic-external'),
  folder: icon('ic-folder'),
};

// ----------------------------------------------------------------------

/**
 * Input nav data is an array of navigation section items used to define the structure and content of a navigation bar.
 * Each section contains a subheader and an array of items, which can include nested children items.
 *
 * Each item can have the following properties:
 * - `title`: The title of the navigation item.
 * - `path`: The URL path the item links to.
 * - `icon`: An optional icon component to display alongside the title.
 * - `info`: Optional additional information to display, such as a label.
 * - `allowedRoles`: An optional array of roles that are allowed to see the item.
 * - `caption`: An optional caption to display below the title.
 * - `children`: An optional array of nested navigation items.
 * - `disabled`: An optional boolean to disable the item.
 * - `deepMatch`: An optional boolean to indicate if the item should match subpaths.
 */
export const navData = (t: (key: string) => string): NavSectionProps['data'] => [
  /**
   * Overview
   */
  {
    subheader: t('distributor.overview'),
    items: [
      {
        title: t('distributor.collection'),
        path: paths.dashboard.artham.collection,
        icon: <Iconify icon="solar:hand-money-bold-duotone" width={24} />,
        primary: true,
      },
      {
        title: t('distributor.manageDealers'),
        path: paths.dashboard.artham.manageDealers,
        icon: <Iconify icon="solar:users-group-rounded-bold-duotone" width={24} />,
        primary: true,
      },
      {
        title: t('distributor.depositToAdmin'),
        path: paths.dashboard.artham.depositToAdmin,
        icon: <Iconify icon="solar:wallet-money-bold-duotone" width={24} />,
        primary: true,
      },
      {
        title: t('distributor.networkActivity'),
        path: paths.dashboard.artham.networkActivity,
        icon: <Iconify icon="solar:pulse-bold-duotone" width={24} />,
      },
      { title: t('distributor.history'), path: paths.dashboard.artham.history, icon: ICONS.file, primary: true },
      {
        title: t('distributor.grievances'),
        path: paths.dashboard.artham.grievances.root,
        icon: <Iconify icon="solar:chat-round-dots-bold-duotone" width={24} />,
      },
      {
        title: t('distributor.kyc'),
        path: paths.dashboard.artham.kyc.root,
        icon: <Iconify icon="solar:shield-check-bold-duotone" width={24} />,
      },
      {
        title: t('distributor.profile'),
        path: paths.dashboard.artham.profile,
        icon: <Iconify icon="solar:user-circle-bold-duotone" width={24} />,
      },
    ],
  },
];
